import { i as isMobile, c as isDesktop } from "./utilities-2Suih3Nm.js";
class CustomAccordion extends HTMLElement {
  constructor() {
    super();
    this.detailsEl = null;
    this.summaryEl = null;
    this.contentEl = null;
    this.isOpen = false;
    this._click = null;
    this._resizeHandler = null;
    this._siblingsHandler = null;
    this.collapseOnDesktop = this.dataset.collapseOnDesktop !== "false";
    this.collapseOnMobile = this.dataset.collapseOnMobile !== "false";
    this.expandByDefault = this.dataset.expandByDefault === "true";
    this.closeSiblings = this.dataset.closeSiblings !== "false";
  }
  connectedCallback() {
    this.detailsEl = this.querySelector("details");
    if (!this.detailsEl) return;
    this.summaryEl = this.detailsEl.querySelector("summary");
    this.contentEl = this.summaryEl.nextElementSibling;
    if (!this.summaryEl || !this.contentEl) return;
    if (!this.contentEl.id) {
      this.contentEl.id = `accordion-content-${this.dataset.index ?? crypto.randomUUID()}`;
    }
    this.summaryEl.setAttribute("aria-controls", this.contentEl.id);
    this._initialOpen = this.detailsEl.hasAttribute("open");
    this._initialActive = this.detailsEl.classList.contains("active");
    this.isOpen = this.detailsEl.open;
    this.summaryEl.setAttribute("aria-expanded", String(this.isOpen));
    this.bindEvents();
    this.bindResize();
    this._siblingsHandler = (e) => {
      if (!this.closeSiblings) return;
      if (e.detail.source === this) return;
      if (!this.isOpen) return;
      this.isOpen = false;
      this._collapse();
      this.summaryEl.setAttribute("aria-expanded", "false");
    };
    document.addEventListener("accordion-siblings-close", this._siblingsHandler);
    if (isMobile() && this.expandByDefault) {
      this.toggle();
    }
  }
  disconnectedCallback() {
    this._teardownTransition();
    if (this._resizeHandler) {
      window.removeEventListener("resize", this._resizeHandler);
      this._resizeHandler = null;
    }
    if (this._click && this.summaryEl) {
      this.summaryEl.removeEventListener("click", this._click);
      this._click = null;
    }
    if (this._siblingsHandler) {
      document.removeEventListener("accordion-siblings-close", this._siblingsHandler);
      this._siblingsHandler = null;
    }
  }
  _teardownTransition() {
    this.contentEl.removeEventListener("transitionend", this._onTransitionEnd);
    this._onTransitionEnd = null;
  }
  handleResize() {
    if (!this.detailsEl || !this.contentEl) return;
    const currentWidth = window.innerWidth;
    if (this._lastWidth === currentWidth) return;
    this._lastWidth = currentWidth;
    this.contentEl.style.maxHeight = "";
    this.contentEl.style.overflow = "";
    const collapse = isDesktop() ? this.collapseOnDesktop : this.collapseOnMobile;
    if (collapse) {
      if (isDesktop()) {
        this.detailsEl.removeAttribute("open");
        this.detailsEl.classList.remove("active");
      } else {
        this.detailsEl[this._initialOpen ? "setAttribute" : "removeAttribute"]("open", "");
        this.detailsEl.classList[this._initialActive ? "add" : "remove"]("active");
      }
      this.isOpen = isDesktop() ? false : this._initialOpen;
    } else {
      this.detailsEl.setAttribute("open", "");
      this.detailsEl.classList.add("active");
      this.isOpen = true;
    }
    this.summaryEl.setAttribute("aria-expanded", String(this.isOpen));
  }
  bindResize() {
    this._lastWidth = window.innerWidth;
    this._resizeTimer = null;
    this._resizeHandler = () => {
      clearTimeout(this._resizeTimer);
      this._resizeTimer = setTimeout(() => this.handleResize(), 150);
    };
    window.addEventListener("resize", this._resizeHandler, { passive: true });
  }
  bindEvents() {
    this._click = (e) => {
      e.preventDefault();
      if (isDesktop() && !this.collapseOnDesktop) return;
      if (isMobile() && !this.collapseOnMobile) return;
      this.toggle();
    };
    this.summaryEl.addEventListener("click", this._click);
  }
  toggle() {
    this._teardownTransition();
    this.isOpen = !this.isOpen;
    if (this.isOpen) {
      if (this.closeSiblings) {
        this.dispatchEvent(new CustomEvent("accordion-siblings-close", {
          bubbles: true,
          composed: true,
          detail: { source: this }
        }));
      }
      this._expand();
    } else {
      this._collapse();
    }
    this.summaryEl.setAttribute("aria-expanded", String(this.isOpen));
    this.dispatchEvent(new CustomEvent("toggle", {
      bubbles: true,
      detail: { current: this.detailsEl, open: this.isOpen }
    }));
  }
  _expand() {
    this.contentEl.style.willChange = "max-height, opacity";
    this.contentEl.style.visibility = "hidden";
    this.contentEl.style.maxHeight = "none";
    const targetHeight = this.contentEl.scrollHeight;
    this.contentEl.style.visibility = "";
    this.contentEl.style.overflow = "hidden";
    this.contentEl.style.maxHeight = "0px";
    this.contentEl.style.opacity = "0";
    this.detailsEl.classList.add("active");
    void this.contentEl.offsetHeight;
    this._onTransitionEnd = (e) => {
      if (e.propertyName !== "max-height") return;
      this._teardownTransition();
      if (this.isOpen) {
        this.contentEl.style.maxHeight = "";
        this.contentEl.style.overflow = "";
        this.contentEl.style.willChange = "";
      }
    };
    this.contentEl.addEventListener("transitionend", this._onTransitionEnd);
    this.detailsEl.open = true;
    requestAnimationFrame(() => {
      this.contentEl.style.maxHeight = `${targetHeight}px`;
      this.contentEl.style.opacity = "1";
    });
  }
  _collapse() {
    this.contentEl.style.willChange = "max-height, opacity";
    const currentHeight = this.contentEl.scrollHeight;
    this.contentEl.style.overflow = "hidden";
    this.contentEl.style.maxHeight = `${currentHeight}px`;
    this.contentEl.style.opacity = "1";
    this.detailsEl.classList.remove("active");
    void this.contentEl.offsetHeight;
    this._onTransitionEnd = (e) => {
      if (e.propertyName !== "max-height") return;
      this._teardownTransition();
      if (!this.isOpen) {
        this.detailsEl.open = false;
        this.contentEl.style.maxHeight = "";
        this.contentEl.style.overflow = "";
        this.contentEl.style.willChange = "";
      }
    };
    this.contentEl.addEventListener("transitionend", this._onTransitionEnd);
    requestAnimationFrame(() => {
      this.contentEl.style.maxHeight = "0px";
      this.contentEl.style.opacity = "0";
    });
  }
}
if (!customElements.get("accordion-component")) {
  customElements.define("accordion-component", CustomAccordion);
}
